/*global gapi:false */

"use strict"

import ErrorCodes from "../../model/domain/error-codes"

class GoogleScopes {
  static get PROFILE_AND_EMAIL() {
    return "profile email"
  }

  static get READ_COURSES_AND_STUDENT_EMAIL() {
    return "https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.rosters.readonly https://www.googleapis.com/auth/classroom.profile.emails"
  }
}

class GoogleDiscoveryDocs {
  static get CLASSROOM_DISCOVERY_DOC() {
    return "https://www.googleapis.com/discovery/v1/apis/classroom/v1/rest"
  }
}

export default class GoogleClientService {
  constructor($q, $log, environment) {
    "ngInject"

    this.$q = $q
    this.$log = $log

    /** @type {environment} */
    this._environment = environment
    /** @type {gapi} */
    this._gapi = null
    /** @type {gapi.auth2.GoogleAuth} */
    this._googleAuth = null
    /** @type {boolean} */
    this._initializationStarted = false

    // Wait for gapi to be loaded before initializing
    this._ensureGapiLoaded()
      .then(() => {
        this._gapi = gapi
        this._initGoogleAuth()
      })
      .catch((err) => {
        this.$log.error("Failed to load Google API:", err)
        this._googleAuthErr = err
      })
  }

  /**
   * Ensures the gapi library is loaded before we try to use it
   * @private
   * @returns {Promise}
   */
  _ensureGapiLoaded(attempts = 0, maxAttempts = 100) {
    // Check if gapi is available
    if (typeof gapi !== "undefined" && gapi.load) {
      this.$log.debug("gapi is loaded and ready")
      return this.$q.resolve()
    }

    if (attempts >= maxAttempts) {
      // 100 attempts * 100ms = 10 seconds timeout
      this.$log.error("Google API script failed to load after 10 seconds")
      let err = new Error(
        "Google API failed to load. This may be caused by:\n" +
          "• Ad blockers or browser extensions\n" +
          "• Network connectivity issues\n" +
          "• Corporate firewall blocking Google services\n" +
          "Please try disabling extensions or using a different network.",
      )
      err.code = ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
      return this.$q.reject(err)
    }

    // Wait 100ms and check again
    if (attempts % 10 === 0 && attempts > 0) {
      this.$log.debug(`Waiting for gapi to load... (attempt ${attempts}/100)`)
    }

    let deferred = this.$q.defer()
    setTimeout(() => {
      this._ensureGapiLoaded(attempts + 1, maxAttempts)
        .then(() => deferred.resolve())
        .catch((err) => deferred.reject(err))
    }, 100)

    return deferred.promise
  }

  /**
   * Initializes google sign in
   * Documentation on the discoveryUrl can be found here: https://developers.google.com/api-client-library/javascript/features/discovery
   * @return Promise<gapi.auth2.GoogleAuth>
   */
  _initGoogleAuth() {
    if (this._initializationStarted) {
      this.$log.warn("Google Auth initialization already started")
      return
    }

    this._initializationStarted = true

    let discoveryUrl = GoogleDiscoveryDocs.CLASSROOM_DISCOVERY_DOC
    let scope = GoogleScopes.PROFILE_AND_EMAIL

    this.$log.debug("Starting Google Auth initialization...")

    this._gapi.load(
      "client:auth2",
      () => {
        this.$log.debug("gapi client:auth2 loaded, initializing client...")

        this._gapi.client
          .init({
            apiKey: this._environment.google.auth.apiKey,
            discoveryDocs: [discoveryUrl],
            client_id: this._environment.google.auth.clientId,
            scope: scope,
          })
          .then(
            () => {
              this._googleAuth = this._gapi.auth2.getAuthInstance()
              this.$log.debug("Google Auth initialized successfully")
            },
            (err) => {
              this._googleAuthErr = err
              this.$log.error("Google Auth initialization failed:", err)
            },
          )
      },
      (err) => {
        // Error handler for gapi.load failure
        this._googleAuthErr = err
        this.$log.error("Failed to load gapi client:auth2:", err)
      },
    )
  }

  /**
   * Ensures Google Auth is ready before attempting sign-in
   * @private
   * @returns {Promise}
   */
  _ensureGoogleAuthReady(attempts = 0, maxAttempts = 150) {
    if (this._googleAuth) {
      this.$log.debug("Google Auth is ready")
      return this.$q.resolve()
    }

    if (this._googleAuthErr) {
      this.$log.error("Google Auth has an error:", this._googleAuthErr)
      let errorMessage = "Google Sign-In failed to initialize."

      // Parse specific error messages from Google
      if (this._googleAuthErr.details) {
        errorMessage += " " + this._googleAuthErr.details
      } else if (this._googleAuthErr.error) {
        if (this._googleAuthErr.error === "idpiframe_initialization_failed") {
          errorMessage =
            "Google Sign-In is blocked by your browser. Please:\n" +
            "• Enable third-party cookies in your browser settings\n" +
            '• Disable "Prevent Cross-Site Tracking" (Safari)\n' +
            "• Try using a different browser or incognito mode"
        } else {
          errorMessage += " Error: " + this._googleAuthErr.error
        }
      }

      let err = new Error(errorMessage)
      err.code = ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
      return this.$q.reject(err)
    }

    if (attempts >= maxAttempts) {
      // 150 attempts * 100ms = 15 seconds timeout
      this.$log.error("Google Auth initialization timed out after 15 seconds")
      let err = new Error(
        "Google Sign-In took too long to load. Please try:\n" +
          "• Refreshing the page\n" +
          "• Disabling browser extensions (especially ad blockers)\n" +
          "• Checking your internet connection\n" +
          "• Using a different browser",
      )
      err.code = ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
      return this.$q.reject(err)
    }

    // Wait 100ms and check again
    if (attempts % 10 === 0 && attempts > 0) {
      this.$log.debug(
        `Waiting for Google Auth... (${Math.round(attempts / 10)} seconds)`,
      )
    }

    let deferred = this.$q.defer()
    setTimeout(() => {
      this._ensureGoogleAuthReady(attempts + 1, maxAttempts)
        .then(() => deferred.resolve())
        .catch((err) => deferred.reject(err))
    }, 100)

    return deferred.promise
  }

  /**
   * Signs in using googleAuth. Returns a promise containing an auth object.
   * @returns Promise<Object>
   */
  signIn() {
    this.$log.debug("signIn() called, ensuring Google Auth is ready...")

    // Wait for Google Auth to be ready (up to 15 seconds)
    return this._ensureGoogleAuthReady().then(() => {
      if (!this.googleAuth) {
        this.$log.error("Google Auth is null after initialization wait")
        let err = new Error(
          this._googleAuthErr?.details ||
            this._googleAuthErr?.error ||
            "Google Sign-In is not available. Please refresh the page and try again.",
        )
        err.code = ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
        return this.$q.reject(err)
      }

      this.$log.debug("Attempting Google sign-in with account selection...")
      let deferred = this.$q.defer()

      // Timeout to prevent infinite hang if popup doesn't open
      let timeoutId = setTimeout(() => {
        this.$log.warn("Google sign-in timed out after 30 seconds")
        let err = new Error(
          "Sign-in timed out. The popup may have been blocked or closed.",
        )
        err.code = ErrorCodes.GOOGLE_POPUP_CLOSED_BY_USER
        deferred.reject(err)
      }, 30000)

      // Attempt sign-in with forced account selection
      this.googleAuth.signIn({ prompt: "select_account" }).then(
        (user) => {
          clearTimeout(timeoutId)
          this.$log.debug("Google sign-in successful")

          // Verify we got a valid token
          let authResponse = user.getAuthResponse()
          if (!authResponse || !authResponse.id_token) {
            this.$log.error("No valid token received from Google")
            let err = new Error(
              "Failed to get valid authentication token from Google",
            )
            err.code = ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
            deferred.reject(err)
            return
          }

          deferred.resolve({
            idToken: authResponse.id_token,
            firstName: user.getBasicProfile().getGivenName(),
            lastName: user.getBasicProfile().getFamilyName(),
            email: user.getBasicProfile().getEmail(),
          })
        },
        (error) => {
          clearTimeout(timeoutId)
          this.$log.warn("Google sign-in rejected:", error)
          let err = new Error(error.error || "Google sign-in failed")
          err.code = error.error || ErrorCodes.GOOGLE_AUTH_SETUP_FAILED
          deferred.reject(err)
        },
      )

      return deferred.promise
    })
  }

  signOut() {
    if (this.googleAuth) {
      this.googleAuth.signOut()
    }
  }

  get hasCourseAndStudentPermissions() {
    let googleAuth = this.googleAuth
    let user = googleAuth && googleAuth.currentUser.get()
    return (
      user && user.hasGrantedScopes(GoogleScopes.READ_COURSES_AND_STUDENT_EMAIL)
    )
  }

  /**
   * Grants scopes to read courses, rosters, and student emails. See more about scopes here: https://developers.google.com/classroom/guides/auth
   *
   * @returns {Promise}
   */
  grantCourseAndStudentPermissions() {
    let deferred = this.$q.defer()

    this.googleAuth.currentUser
      .get()
      .grant({
        scope: GoogleScopes.READ_COURSES_AND_STUDENT_EMAIL,
      })
      .then(() => {
        deferred.resolve()
      })
      .catch((err) => {
        this.$log.error(err)
        deferred.reject(err)
      })

    return deferred.promise
  }

  /**
   * Gets the Google course information for the current user.
   * @param nextPageToken
   * @param courses
   * @return {Promise}
   */
  getCourses(nextPageToken, courses = []) {
    let request = {
      pageSize: 20,
      teacherId: this.userId,
    }

    if (nextPageToken) {
      request.pageToken = nextPageToken
    }

    return this._gapi.client.classroom.courses
      .list(request)
      .then((response) => {
        let nextPageToken = response.result.nextPageToken
        let retrievedCourses = response.result.courses || []

        if (nextPageToken) {
          return this.getCourses(
            nextPageToken,
            retrievedCourses.concat(courses),
          )
        } else {
          return retrievedCourses.concat(courses)
        }
      })
      .catch(() => {
        this.$log.error("Could not get Google courses.")
      })
  }

  /**
   * Returns the google user id
   * @return {string}
   */
  get userId() {
    let user = this.googleAuth.currentUser.get()
    return user && user.getId()
  }

  /**
   * Makes a request to the Google Classroom API to get the students from the course in the specified Google Course Id.
   *
   * @param googleCourseId {string}
   * @param [pageToken] {string}
   * @param [students] {object[]}
   * @returns Promise<object[]>
   */
  getGoogleCourseStudents(googleCourseId, pageToken, students = []) {
    return this._getGoogleCourseStudents(googleCourseId, pageToken).then(
      (data) => {
        let nextPageToken = data.result.nextPageToken
        let retrievedStudents = data.result.students || []

        if (nextPageToken) {
          return this.getGoogleCourseStudents(
            googleCourseId,
            nextPageToken,
            retrievedStudents.concat(students),
          )
        } else {
          return retrievedStudents.concat(students)
        }
      },
    )
  }

  /**
   * Gets students for a google course
   * See docs for details https://developers.google.com/classroom/reference/rest/v1/courses.students/list
   * @param googleCourseId {string}
   * @param pageToken {string|undefined}
   * @return {Promise<object[]>}
   * @private
   */
  _getGoogleCourseStudents(googleCourseId, pageToken) {
    let path = `https://classroom.googleapis.com/v1/courses/${googleCourseId}/students`

    if (pageToken) {
      path = `${path}?pageToken=${pageToken}`
    }

    return this._gapi.client.request({ path })
  }

  get googleAuth() {
    return this._googleAuth
  }
}
